import React from 'react'
import EmojiPicker from '../EmojiPicker'

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function getEmptySection(type = 'text') {
  if (type === 'icon-grid') {
    return { type, title: '', columns: 3, items: [{ icon: '🔍', title: '', description: '' }] }
  }
  if (type === 'process-steps') {
    return { type, title: '', layout: 'horizontal', steps: [{ number: 1, icon: '📝', title: '', description: '' }] }
  }
  if (type === 'stats') {
    return { type, title: '', items: [{ label: '', value: '' }] }
  }
  if (type === 'faq') {
    return { type, title: '', items: [{ question: '', answer: '' }] }
  }
  if (type === 'testimonial') {
    return { type, title: '', items: [{ name: '', subtitle: '', content: '' }] }
  }
  if (type === 'comparison') {
    return {
      type,
      title: '',
      before: { label: 'Before', title: '', description: '' },
      after: { label: 'After', title: '', description: '' },
    }
  }
  if (type === 'image' || type === 'infographic') {
    return { type, title: '', fileId: '', caption: '' }
  }
  if (type === 'video') {
    return { type, title: '', url: '' }
  }
  return { type: 'text', title: '', content: '' }
}

const SECTION_OPTIONS = [
  { value: 'text', label: '텍스트' },
  { value: 'infographic', label: '인포그래픽' },
  { value: 'icon-grid', label: '아이콘 그리드' },
  { value: 'process-steps', label: '프로세스 단계' },
  { value: 'stats', label: '통계' },
  { value: 'image', label: '이미지' },
  { value: 'faq', label: 'FAQ' },
  { value: 'testimonial', label: '고객 후기' },
  { value: 'comparison', label: '비교' },
  { value: 'video', label: '영상' },
]

function SectionOrderHandle({ index, dragging = false }) {
  return (
    <div
      className={`flex min-w-[76px] cursor-grab items-center gap-2 rounded border px-2 py-1 text-xs font-semibold transition active:cursor-grabbing ${
        dragging
          ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10'
          : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
      }`}
      title="드래그해서 순서를 바꿀 수 있습니다."
    >
      <span className="tracking-[0.2em] text-[10px]">:::</span>
      <span>순서 {index + 1}</span>
    </div>
  )
}

function resolveDropPosition(event) {
  const rect = event.currentTarget.getBoundingClientRect()
  const midpoint = rect.top + rect.height / 2
  return event.clientY <= midpoint ? 'before' : 'after'
}

export default function CategoryLandingSectionBlockEditor({
  sections = [],
  onChange,
  title = '본문 섹션',
  addButtonLabel = '섹션 추가',
  useRichLabels = false,
  allowMediaUpload = false,
  onUploadMedia,
}) {
  const [emojiPickerOpen, setEmojiPickerOpen] = React.useState({ sectionIdx: null, itemIdx: null, type: null })
  const [draggingIndex, setDraggingIndex] = React.useState(-1)
  const [dragOverIndex, setDragOverIndex] = React.useState(-1)
  const [dragOverPosition, setDragOverPosition] = React.useState('before')

  React.useEffect(() => {
    if (emojiPickerOpen.sectionIdx !== null) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.emoji-picker-container')) {
          setEmojiPickerOpen({ sectionIdx: null, itemIdx: null, type: null })
        }
      }
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
    return undefined
  }, [emojiPickerOpen])

  const setSections = (next) => {
    onChange?.(next)
  }

  const updateSection = (idx, key, value) => {
    setSections(sections.map((section, sectionIdx) => (sectionIdx === idx ? { ...section, [key]: value } : section)))
  }

  const reorderSections = (fromIndex, toIndex, position = 'before') => {
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return
    const next = [...sections]
    const [moved] = next.splice(fromIndex, 1)
    const adjustedIndex = fromIndex < toIndex ? toIndex - 1 : toIndex
    const insertIndex = position === 'after' ? adjustedIndex + 1 : adjustedIndex
    next.splice(insertIndex, 0, moved)
    setSections(next)
  }

  return (
    <div className="rounded-xl border p-4 space-y-3 bg-white">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-semibold text-sm">{title}</div>
          <div className="mt-1 text-xs text-slate-500">각 섹션의 `순서` 버튼을 드래그해서 배치 순서를 바꿀 수 있습니다.</div>
        </div>
        <button type="button" className="rounded border border-teal-200 bg-teal-50 px-2 py-1 text-xs font-semibold text-teal-800 hover:bg-teal-100" onClick={() => setSections([...(sections || []), getEmptySection('text')])}>
          {addButtonLabel}
        </button>
      </div>
      {(sections || []).map((sec, idx) => {
        const type = sec?.type || 'text'
        return (
          <div
            key={idx}
            className={`relative rounded border p-3 space-y-2 bg-slate-50 transition-all ${
              draggingIndex === idx ? 'scale-[0.995] opacity-60 shadow-none' : ''
            } ${
              dragOverIndex === idx && draggingIndex !== idx ? 'border-blue-400 bg-blue-50/30 ring-2 ring-blue-100' : ''
            }`}
            onDragOver={(e) => {
              if (draggingIndex < 0) return
              e.preventDefault()
              const nextPosition = resolveDropPosition(e)
              if (dragOverIndex !== idx) setDragOverIndex(idx)
              if (dragOverPosition !== nextPosition) setDragOverPosition(nextPosition)
            }}
            onDrop={(e) => {
              e.preventDefault()
              reorderSections(draggingIndex, idx, dragOverPosition)
              setDraggingIndex(-1)
              setDragOverIndex(-1)
              setDragOverPosition('before')
            }}
            onDragEnd={() => {
              setDraggingIndex(-1)
              setDragOverIndex(-1)
              setDragOverPosition('before')
            }}
          >
            {dragOverIndex === idx && draggingIndex !== idx ? (
              <div
                className={`pointer-events-none absolute inset-x-3 z-10 flex items-center gap-2 ${
                  dragOverPosition === 'after' ? 'bottom-0 translate-y-1/2' : 'top-0 -translate-y-1/2'
                }`}
              >
                <div className="h-[3px] flex-1 rounded-full bg-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.12)]" />
                <div className="shrink-0 rounded-full bg-blue-600 px-3 py-1 text-[11px] font-semibold text-white shadow-sm">
                  {dragOverPosition === 'after' ? '아래로 이동' : '위로 이동'}
                </div>
                <div className="h-[3px] flex-1 rounded-full bg-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.12)]" />
              </div>
            ) : null}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div
                  draggable
                  onDragStart={(e) => {
                    setDraggingIndex(idx)
                    setDragOverIndex(idx)
                    try {
                      e.dataTransfer.effectAllowed = 'move'
                      e.dataTransfer.setData('text/plain', String(idx))
                    } catch {
                      // noop
                    }
                  }}
                >
                  <SectionOrderHandle index={idx} dragging={draggingIndex === idx} />
                </div>
                <div className="text-xs text-slate-500 font-medium">섹션 {idx + 1}</div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="rounded border px-2 py-1 text-xs hover:bg-white disabled:opacity-40"
                  disabled={idx === 0}
                  onClick={() => {
                    const next = [...sections]
                    const temp = next[idx - 1]
                    next[idx - 1] = next[idx]
                    next[idx] = temp
                    setSections(next)
                  }}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="rounded border px-2 py-1 text-xs hover:bg-white disabled:opacity-40"
                  disabled={idx === sections.length - 1}
                  onClick={() => {
                    const next = [...sections]
                    const temp = next[idx + 1]
                    next[idx + 1] = next[idx]
                    next[idx] = temp
                    setSections(next)
                  }}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="rounded border px-2 py-1 text-xs hover:bg-white"
                  onClick={() => {
                    const next = [...sections]
                    next.splice(idx + 1, 0, clone(sec))
                    setSections(next)
                  }}
                >
                  복사
                </button>
                <button
                  type="button"
                  className="rounded border px-2 py-1 text-xs text-red-600 hover:bg-white"
                  onClick={() => {
                    if (window.confirm('이 섹션을 삭제하시겠습니까?')) {
                      setSections(sections.filter((_, sectionIdx) => sectionIdx !== idx))
                    }
                  }}
                >
                  삭제
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <select
                className="rounded border px-2 py-2 text-sm bg-white"
                value={type}
                onChange={(e) => {
                  const next = [...sections]
                  next[idx] = getEmptySection(e.target.value)
                  setSections(next)
                }}
              >
                {SECTION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {useRichLabels && option.value === 'text'
                      ? '텍스트 (짧은 설명)'
                      : useRichLabels && option.value === 'infographic'
                        ? '인포그래픽 (권장)'
                        : useRichLabels && option.value === 'icon-grid'
                          ? '아이콘 그리드 (혜택/특징)'
                          : useRichLabels && option.value === 'process-steps'
                            ? '프로세스 단계 (진행 과정)'
                            : useRichLabels && option.value === 'stats'
                              ? '통계 (수치 표시)'
                              : useRichLabels && option.value === 'image'
                                ? '이미지 (일반 사진)'
                                : useRichLabels && option.value === 'faq'
                                  ? 'FAQ (자주 묻는 질문)'
                                  : option.label}
                  </option>
                ))}
              </select>
              {type !== 'stats' && type !== 'comparison' && (
                <input className="rounded border px-2 py-2 text-sm bg-white" placeholder="섹션 제목" value={sec.title || ''} onChange={(e) => updateSection(idx, 'title', e.target.value)} />
              )}
            </div>

            {type === 'text' && (
              <div>
                <div className="text-xs text-slate-500 bg-amber-50 p-2 rounded mb-2">
                  <strong>텍스트:</strong> 짧고 명확한 문구를 사용하세요. 긴 설명은 인포그래픽 섹션으로 대체하는 것을 권장합니다.
                </div>
                <textarea className="w-full rounded border px-2 py-2 text-sm min-h-[80px] bg-white" placeholder="본문 내용" value={sec.content || ''} onChange={(e) => updateSection(idx, 'content', e.target.value)} maxLength={500} />
              </div>
            )}

            {(type === 'image' || type === 'infographic') && (
              <div>
                <div className="text-xs text-slate-500 bg-blue-50 p-2 rounded mb-2">
                  <strong>{type === 'infographic' ? '인포그래픽' : '이미지'}:</strong> 핵심 메시지를 시각적으로 표현하는 이미지를 사용하세요.
                </div>
                <div className="flex gap-2">
                  <input
                    className="w-full rounded border px-2 py-2 text-sm bg-white"
                    placeholder={type === 'infographic' ? '인포그래픽 이미지 파일 ID' : '이미지 파일 ID(fileId)'}
                    value={sec.fileId || ''}
                    onChange={(e) => updateSection(idx, 'fileId', e.target.value)}
                  />
                  {allowMediaUpload && onUploadMedia && (
                    <label className="rounded border px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 bg-white">
                      파일선택
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => onUploadMedia(e.target.files?.[0], 'sectionImage', idx)} />
                    </label>
                  )}
                </div>
                <input className="mt-2 w-full rounded border px-2 py-2 text-sm bg-white" placeholder="짧은 설명 (선택)" value={sec.caption || ''} onChange={(e) => updateSection(idx, 'caption', e.target.value)} maxLength={200} />
              </div>
            )}

            {type === 'icon-grid' && (
              <div className="space-y-2">
                <div className="text-xs text-slate-500 bg-blue-50 p-2 rounded">
                  <strong>아이콘 그리드:</strong> 주요 혜택이나 특징을 아이콘과 함께 시각적으로 표현합니다.
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-slate-600">열 수:</label>
                  <select className="rounded border px-2 py-1 text-sm bg-white" value={sec.columns || 3} onChange={(e) => updateSection(idx, 'columns', Number(e.target.value))}>
                    <option value={2}>2열</option>
                    <option value={3}>3열</option>
                    <option value={4}>4열</option>
                  </select>
                  <button type="button" className="ml-auto rounded border px-2 py-1 text-xs" onClick={() => updateSection(idx, 'items', [...(Array.isArray(sec.items) ? sec.items : []), { icon: '🔍', title: '', description: '' }])}>
                    항목 추가
                  </button>
                </div>
                {(Array.isArray(sec.items) ? sec.items : []).map((item, itemIdx) => (
                  <div key={itemIdx} className="rounded border p-2 bg-slate-50 space-y-1">
                    <div className="flex gap-2">
                      <div className="relative">
                        <input
                          className="w-20 rounded border px-2 py-1 text-sm bg-white"
                          placeholder="아이콘"
                          value={item.icon || ''}
                          onChange={(e) => {
                            const items = [...(sec.items || [])]
                            items[itemIdx] = { ...items[itemIdx], icon: e.target.value }
                            updateSection(idx, 'items', items)
                          }}
                        />
                        <button
                          type="button"
                          className="absolute right-1 top-1 text-xs px-1 py-0.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100"
                          onClick={() => setEmojiPickerOpen({ sectionIdx: idx, itemIdx, type: 'icon-grid' })}
                        >
                          😀
                        </button>
                        {emojiPickerOpen.sectionIdx === idx && emojiPickerOpen.itemIdx === itemIdx && emojiPickerOpen.type === 'icon-grid' && (
                          <div className="absolute left-0 top-8 z-50 emoji-picker-container">
                            <EmojiPicker
                              value={item.icon || ''}
                              onChange={(emoji) => {
                                const items = [...(sec.items || [])]
                                items[itemIdx] = { ...items[itemIdx], icon: emoji }
                                updateSection(idx, 'items', items)
                                setEmojiPickerOpen({ sectionIdx: null, itemIdx: null, type: null })
                              }}
                              onClose={() => setEmojiPickerOpen({ sectionIdx: null, itemIdx: null, type: null })}
                            />
                          </div>
                        )}
                      </div>
                      <input className="flex-1 rounded border px-2 py-1 text-sm bg-white" placeholder="제목" value={item.title || ''} onChange={(e) => {
                        const items = [...(sec.items || [])]
                        items[itemIdx] = { ...items[itemIdx], title: e.target.value }
                        updateSection(idx, 'items', items)
                      }} />
                      <button type="button" className="text-xs text-red-600" onClick={() => {
                        const items = [...(sec.items || [])]
                        items.splice(itemIdx, 1)
                        updateSection(idx, 'items', items)
                      }}>삭제</button>
                    </div>
                    <input className="w-full rounded border px-2 py-1 text-sm bg-white" placeholder="1줄 설명" value={item.description || ''} onChange={(e) => {
                      const items = [...(sec.items || [])]
                      items[itemIdx] = { ...items[itemIdx], description: e.target.value }
                      updateSection(idx, 'items', items)
                    }} maxLength={100} />
                  </div>
                ))}
              </div>
            )}

            {type === 'process-steps' && (
              <div className="space-y-2">
                <div className="text-xs text-slate-500 bg-blue-50 p-2 rounded">
                  <strong>프로세스 단계:</strong> 서비스 진행 과정을 단계별로 설명합니다.
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-slate-600">레이아웃:</label>
                  <select className="rounded border px-2 py-1 text-sm bg-white" value={sec.layout || 'horizontal'} onChange={(e) => updateSection(idx, 'layout', e.target.value)}>
                    <option value="horizontal">가로</option>
                    <option value="vertical">세로</option>
                  </select>
                  <button type="button" className="ml-auto rounded border px-2 py-1 text-xs" onClick={() => updateSection(idx, 'steps', [...(Array.isArray(sec.steps) ? sec.steps : []), { number: (sec?.steps?.length || 0) + 1, icon: '📝', title: '', description: '' }])}>
                    단계 추가
                  </button>
                </div>
                {(Array.isArray(sec.steps) ? sec.steps : []).map((step, stepIdx) => (
                  <div key={stepIdx} className="rounded border p-2 bg-slate-50 space-y-1">
                    <div className="flex gap-2">
                      <input type="number" className="w-16 rounded border px-2 py-1 text-sm bg-white" placeholder="번호" value={step.number || stepIdx + 1} onChange={(e) => {
                        const steps = [...(sec.steps || [])]
                        steps[stepIdx] = { ...steps[stepIdx], number: Number(e.target.value) }
                        updateSection(idx, 'steps', steps)
                      }} />
                      <div className="relative">
                        <input className="w-20 rounded border px-2 py-1 text-sm bg-white" placeholder="아이콘" value={step.icon || ''} onChange={(e) => {
                          const steps = [...(sec.steps || [])]
                          steps[stepIdx] = { ...steps[stepIdx], icon: e.target.value }
                          updateSection(idx, 'steps', steps)
                        }} />
                        <button
                          type="button"
                          className="absolute right-1 top-1 text-xs px-1 py-0.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100"
                          onClick={() => setEmojiPickerOpen({ sectionIdx: idx, itemIdx: stepIdx, type: 'process-steps' })}
                        >
                          😀
                        </button>
                        {emojiPickerOpen.sectionIdx === idx && emojiPickerOpen.itemIdx === stepIdx && emojiPickerOpen.type === 'process-steps' && (
                          <div className="absolute left-0 top-8 z-50 emoji-picker-container">
                            <EmojiPicker
                              value={step.icon || ''}
                              onChange={(emoji) => {
                                const steps = [...(sec.steps || [])]
                                steps[stepIdx] = { ...steps[stepIdx], icon: emoji }
                                updateSection(idx, 'steps', steps)
                                setEmojiPickerOpen({ sectionIdx: null, itemIdx: null, type: null })
                              }}
                              onClose={() => setEmojiPickerOpen({ sectionIdx: null, itemIdx: null, type: null })}
                            />
                          </div>
                        )}
                      </div>
                      <input className="flex-1 rounded border px-2 py-1 text-sm bg-white" placeholder="단계 제목" value={step.title || ''} onChange={(e) => {
                        const steps = [...(sec.steps || [])]
                        steps[stepIdx] = { ...steps[stepIdx], title: e.target.value }
                        updateSection(idx, 'steps', steps)
                      }} />
                      <button type="button" className="text-xs text-red-600" onClick={() => {
                        const steps = [...(sec.steps || [])]
                        steps.splice(stepIdx, 1)
                        updateSection(idx, 'steps', steps)
                      }}>삭제</button>
                    </div>
                    <input className="w-full rounded border px-2 py-1 text-sm bg-white" placeholder="1줄 설명" value={step.description || ''} onChange={(e) => {
                      const steps = [...(sec.steps || [])]
                      steps[stepIdx] = { ...steps[stepIdx], description: e.target.value }
                      updateSection(idx, 'steps', steps)
                    }} maxLength={100} />
                  </div>
                ))}
              </div>
            )}

            {type === 'faq' && (
              <div className="space-y-2">
                <div className="text-xs text-slate-500 bg-blue-50 p-2 rounded">
                  <strong>FAQ:</strong> 자주 받는 질문을 짧게 정리하세요.
                </div>
                <div className="flex items-center justify-end">
                  <button type="button" className="rounded border border-teal-200 bg-teal-50 px-2 py-1 text-xs font-semibold text-teal-800 hover:bg-teal-100" onClick={() => updateSection(idx, 'items', [...(Array.isArray(sec.items) ? sec.items : []), { question: '', answer: '' }])}>
                    질문 추가
                  </button>
                </div>
                {(Array.isArray(sec.items) ? sec.items : []).map((item, itemIdx) => (
                  <div key={itemIdx} className="rounded border p-2 bg-slate-50 space-y-2">
                    <div className="flex items-center gap-2">
                      <input className="flex-1 rounded border px-2 py-2 text-sm bg-white" placeholder="질문" value={item.question || ''} onChange={(e) => {
                        const items = [...(sec.items || [])]
                        items[itemIdx] = { ...items[itemIdx], question: e.target.value }
                        updateSection(idx, 'items', items)
                      }} />
                      <button type="button" className="text-xs text-red-600" onClick={() => {
                        const items = [...(sec.items || [])]
                        items.splice(itemIdx, 1)
                        updateSection(idx, 'items', items)
                      }}>삭제</button>
                    </div>
                    <textarea className="w-full rounded border px-2 py-2 text-sm min-h-[80px] bg-white" placeholder="답변" value={item.answer || ''} onChange={(e) => {
                      const items = [...(sec.items || [])]
                      items[itemIdx] = { ...items[itemIdx], answer: e.target.value }
                      updateSection(idx, 'items', items)
                    }} maxLength={300} />
                  </div>
                ))}
              </div>
            )}

            {type === 'testimonial' && (
              <div className="space-y-2">
                <div className="text-xs text-slate-500 bg-blue-50 p-2 rounded">
                  <strong>고객 후기:</strong> 후기 문구는 짧고 신뢰감 있게 정리하세요.
                </div>
                <div className="flex items-center justify-end">
                  <button type="button" className="rounded border px-2 py-1 text-xs" onClick={() => updateSection(idx, 'items', [...(Array.isArray(sec.items) ? sec.items : []), { name: '', subtitle: '', content: '' }])}>
                    후기 추가
                  </button>
                </div>
                {(Array.isArray(sec.items) ? sec.items : []).map((item, itemIdx) => (
                  <div key={itemIdx} className="rounded border p-2 bg-slate-50 space-y-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input className="rounded border px-2 py-2 text-sm bg-white" placeholder="고객명" value={item.name || ''} onChange={(e) => {
                        const items = [...(sec.items || [])]
                        items[itemIdx] = { ...items[itemIdx], name: e.target.value }
                        updateSection(idx, 'items', items)
                      }} />
                      <div className="flex gap-2">
                        <input className="flex-1 rounded border px-2 py-2 text-sm bg-white" placeholder="보조 설명(선택)" value={item.subtitle || ''} onChange={(e) => {
                          const items = [...(sec.items || [])]
                          items[itemIdx] = { ...items[itemIdx], subtitle: e.target.value }
                          updateSection(idx, 'items', items)
                        }} />
                        <button type="button" className="text-xs text-red-600" onClick={() => {
                          const items = [...(sec.items || [])]
                          items.splice(itemIdx, 1)
                          updateSection(idx, 'items', items)
                        }}>삭제</button>
                      </div>
                    </div>
                    <textarea className="w-full rounded border px-2 py-2 text-sm min-h-[90px] bg-white" placeholder="후기 내용" value={item.content || ''} onChange={(e) => {
                      const items = [...(sec.items || [])]
                      items[itemIdx] = { ...items[itemIdx], content: e.target.value }
                      updateSection(idx, 'items', items)
                    }} maxLength={400} />
                  </div>
                ))}
              </div>
            )}

            {type === 'stats' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <input className="rounded border px-2 py-2 text-sm flex-1 mr-2" placeholder="섹션 제목" value={sec?.title || ''} onChange={(e) => updateSection(idx, 'title', e.target.value)} />
                  <button type="button" className="rounded border px-2 py-1 text-xs" onClick={() => updateSection(idx, 'items', [...(Array.isArray(sec?.items) ? sec.items : []), { label: '', value: '' }])}>
                    항목 추가
                  </button>
                </div>
                {(Array.isArray(sec?.items) ? sec.items : []).map((item, itemIdx) => (
                  <div key={itemIdx} className="grid grid-cols-1 md:grid-cols-2 gap-2 rounded border p-2 bg-slate-50">
                    <input className="rounded border px-2 py-2 text-sm" placeholder="라벨" value={item?.label || ''} onChange={(e) => {
                      const items = [...sec.items]
                      items[itemIdx] = { ...items[itemIdx], label: e.target.value }
                      updateSection(idx, 'items', items)
                    }} />
                    <div className="flex gap-2">
                      <input className="flex-1 rounded border px-2 py-2 text-sm" placeholder="값" value={item?.value || ''} onChange={(e) => {
                        const items = [...sec.items]
                        items[itemIdx] = { ...items[itemIdx], value: e.target.value }
                        updateSection(idx, 'items', items)
                      }} />
                      <button type="button" className="text-xs text-red-600" onClick={() => {
                        const items = [...sec.items]
                        items.splice(itemIdx, 1)
                        updateSection(idx, 'items', items)
                      }}>삭제</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {type === 'comparison' && (
              <div className="space-y-2">
                <input className="w-full rounded border px-2 py-2 text-sm" placeholder="섹션 제목" value={sec?.title || ''} onChange={(e) => updateSection(idx, 'title', e.target.value)} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="rounded border p-2 bg-slate-50 space-y-2">
                    <div className="text-xs font-semibold text-slate-500">비교 전</div>
                    <input className="w-full rounded border px-2 py-2 text-sm" placeholder="배지" value={sec?.before?.label || ''} onChange={(e) => updateSection(idx, 'before', { ...(sec.before || {}), label: e.target.value })} />
                    <input className="w-full rounded border px-2 py-2 text-sm" placeholder="제목" value={sec?.before?.title || ''} onChange={(e) => updateSection(idx, 'before', { ...(sec.before || {}), title: e.target.value })} />
                    <textarea className="w-full rounded border px-2 py-2 min-h-[80px] text-sm" placeholder="설명" value={sec?.before?.description || ''} onChange={(e) => updateSection(idx, 'before', { ...(sec.before || {}), description: e.target.value })} />
                  </div>
                  <div className="rounded border p-2 bg-slate-50 space-y-2">
                    <div className="text-xs font-semibold text-slate-500">비교 후</div>
                    <input className="w-full rounded border px-2 py-2 text-sm" placeholder="배지" value={sec?.after?.label || ''} onChange={(e) => updateSection(idx, 'after', { ...(sec.after || {}), label: e.target.value })} />
                    <input className="w-full rounded border px-2 py-2 text-sm" placeholder="제목" value={sec?.after?.title || ''} onChange={(e) => updateSection(idx, 'after', { ...(sec.after || {}), title: e.target.value })} />
                    <textarea className="w-full rounded border px-2 py-2 min-h-[80px] text-sm" placeholder="설명" value={sec?.after?.description || ''} onChange={(e) => updateSection(idx, 'after', { ...(sec.after || {}), description: e.target.value })} />
                  </div>
                </div>
              </div>
            )}

            {type === 'video' && (
              <div className="flex gap-2">
                <input className="w-full rounded border px-2 py-2 text-sm bg-white" placeholder="영상 파일 ID(url)" value={sec.url || ''} onChange={(e) => updateSection(idx, 'url', e.target.value)} />
                {allowMediaUpload && onUploadMedia && (
                  <label className="rounded border px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 bg-white">
                    파일선택
                    <input type="file" accept="video/*" className="hidden" onChange={(e) => onUploadMedia(e.target.files?.[0], 'sectionVideo', idx)} />
                  </label>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
